import {
  // Form,
  Formik,
  FormikHelpers,
  FormikProps,
  FormikValues,
} from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { ButtonProps, Form } from 'antd';
import FormObserver from './FormObserver';
import { StepsProps, Steps, Button } from 'antd';
import classes from './Style.module.scss';
import cn from 'classnames';
import * as Yup from 'yup';
import { useScrollToTop } from 'app/hooks/useScrollToTop';

type StepProp = {
  key: string;
  title: string;
  content: React.ReactNode;
};

type FormStepperProps<T = FormikValues> = {
  steps: StepProp[];
  initialValues?: T;
  className?: string;
  enableReinitialize?: boolean;
  saveButtonProps?: ButtonProps;
  nextButtonProps?: ButtonProps;
  onSubmit: (
    values: FormikValues,
    meta: {
      current: number;
      action: string;
      formikAction: FormikHelpers<FormikValues>;
    },
  ) => void;
  onValuesChange?: (
    valueChanged: FormikValues,
    allValues: FormikValues,
    prevValues: FormikValues,
    formik: FormikProps<FormikValues>,
  ) => void;
  stepProps?: Omit<StepsProps, 'items' | 'onChange'>;
  shouldNext?: (current, next) => boolean;
  loading?: boolean;
  validationStepSchema?: (current: number) => Yup.Schema;
  onChangeStep?: (current: number, next: number) => boolean;
};

export default function FormStepper(props: FormStepperProps) {
  const {
    steps,
    className,
    stepProps,
    initialValues,
    onSubmit,
    saveButtonProps,
    nextButtonProps,
    onValuesChange,
    shouldNext,
    enableReinitialize,
    validationStepSchema,
    onChangeStep,
  } = props;
  const action = useRef(null);
  const [current, setCurrent] = useState(0);
  const scrollToTop = useScrollToTop();
  const handleValuesChange = (changed, all, prev, formik) => {
    if (onValuesChange) {
      onValuesChange(changed, all, prev, formik);
    }
  };

  const handleChangeStep = (next) => {
    if (onChangeStep) {
      if (onChangeStep(current, next)) {
        setCurrent(next);
      }
      return;
    }
    setCurrent(next);
  };

  const handleNext = (formikProps) => async (e) => {
    try {
      e.preventDefault();
      action.current = current >= steps.length - 1 ? 'submit' : 'next';
      await formikProps.submitForm();
      if (action.current === 'submit') return;
      scrollToTop();
      if (shouldNext) {
        if (shouldNext(current, current + 1)) {
          setTimeout(() => {
            setCurrent(current + 1);
          }, 250);
        }
      } else {
        setTimeout(() => {
          setCurrent(current + 1);
        }, 250);
      }
    } catch (error) {
      console.error('Form Stepper - ERROR', error);
    }
  };

  const handleSave = (formikProps) => async (e) => {
    try {
      e.preventDefault();
      action.current = 'save';
      await formikProps.submitForm();
    } catch (error) {
      console.error('Form Stepper - ERROR', error);
    }
  };

  return (
    <div className={cn(classes.formStepper, className)}>
      <Steps
        className={cn(classes.steps)}
        items={steps}
        current={current}
        onChange={handleChangeStep}
        {...stepProps}
      />
      <Formik
        validationSchema={
          validationStepSchema ? validationStepSchema(current) : undefined
        }
        enableReinitialize={enableReinitialize}
        initialValues={initialValues}
        onSubmit={(values, actions) => {
          onSubmit(values, {
            current,
            action: action.current,
            formikAction: actions,
          });
        }}
      >
        {(formikProps) => {
          return (
            <Form className="form" layout="vertical">
              <FormObserver onChange={handleValuesChange} />
              <div className={classes.body}>
                {React.Children.map(steps[current]?.content, (child) => {
                  if (typeof child === 'function') {
                    return (child as any)({ formik: formikProps });
                  }
                  return React.cloneElement(child as any, {
                    formik: formikProps,
                  });
                })}
              </div>

              <div className={classes.footer}>
                <div className={classes.actions}>
                  <Button
                    loading={formikProps.isSubmitting}
                    type="primary"
                    ghost
                    onClick={handleSave(formikProps)}
                    {...saveButtonProps}
                  >
                    Save
                  </Button>
                  <Button
                    loading={formikProps.isSubmitting}
                    type="primary"
                    onClick={handleNext(formikProps)}
                    {...nextButtonProps}
                  >
                    {current >= steps.length - 1 ? 'Submit' : 'Next'}
                  </Button>
                </div>
                {/* <div>last saved</div> */}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
